import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  throwError
} from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { ConnectedUserService } from 'app/core/user/connected-user.service';
import { environment } from 'environments/environment';
import { Store } from '@ngrx/store';
import { setConnectedUser } from 'app/_store/userStore/user.actions';
import { UserStore } from '../user/user.types';
import { UserProfile } from 'app/shared/models/profiles-permissions/userProfile.model';
import { UserProfilePermission } from 'app/shared/models/profiles-permissions/userProfilePermission.model';
import { NavigationService } from '../navigation/navigation.service';
import { UserUserProfileService } from '../../shared/services/actors-users/UserUserProfile.service';
import { NavigationMockApi } from 'app/mock-api/common/navigation/api';
import { HttpHeadersUtil } from 'app/shared/utils/http-headers.util';

@Injectable()
export class AuthService {
  private authUri: string = environment.IDENTITY_URL;
  private _authenticated: boolean = false;
  token: string;
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  /**
   * Constructor
   */
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _userService: ConnectedUserService,
    private readonly _userProfileService: UserUserProfileService,
    private readonly _navigationService: NavigationService,
    private readonly _navigationMockApi: NavigationMockApi,
    private readonly _store: Store
  ) {
    this.token = localStorage.getItem('accessToken') ?? '';
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    // Dispatch l'action pour mettre à jour le token
    localStorage.setItem('accessToken', token);
    // Notify the navigation service
    this._navigationService.updateNavigation();
  }

  /**
   * Access token as Observable
   */
  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  clearAccessToken() {
    // Dispatch l'action pour effacer le token
    localStorage.removeItem('accessToken');
  }

  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
    // Notify the navigation service
    this._navigationService.updateNavigation();
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    let queryParam = '?email=' + email + '&platform=tera';
    return this._httpClient.post(this.authUri + 'api/auth/forgot-password' + queryParam, {});
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/reset-password', password);
  }

  resetUserPassword(user: any): Observable<any> {
    return this._httpClient.post(
      this.authUri + 'api/admin/create-acep-user',
      user,
       HttpHeadersUtil.prepareDefaultHeaders()
    );
  }
  /**
   * Decode JWT token
   *
   * @param token
   */
  // public decodeJwt(token: string): any {
  //   const jwtData = token.split('.')[1];
  //   const decodedJwtJsonData = window.atob(jwtData);
  //   return JSON.parse(decodedJwtJsonData);
  // }
  public decodeJwt(token: string): any {
    const jwtData = token.split('.')[1];
    // Correction du format Base64 pour le rendre compatible avec atob
    let base64 = jwtData.replace(/-/g, '+').replace(/_/g, '/');
    // Ajout du padding '=' si besoin
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    const decodedJwtJsonData = window.atob(base64);
    return JSON.parse(decodedJwtJsonData);
  }

  /**
   * Merge user profiles and permissions into a single array
   *
   * @param userProfiles
   */
  mergePermissions(userProfiles: UserProfile[]): UserProfilePermission[] {
    return userProfiles
      .flatMap(profile => profile.permissions)
      .filter(permission => permission.accessLevel?.length > 0)
      .reduce((acc, permission) => {
        const existingPermission = acc.find(
          p => p.teraComponent?.appComponentId === permission.teraComponent?.appComponentId
        );
        if (existingPermission) {
          existingPermission.accessLevel = [
            ...new Set([...existingPermission.accessLevel, ...permission.accessLevel])
          ];
        } else {
          acc.push({ ...permission });
        }
        return acc;
      }, []);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { username: string; password: string }): Observable<any> {
    if (this._authenticated) {
      return throwError(() => new Error('User is already logged in.'));
    }

    return this._httpClient.post(this.authUri + 'api/auth/sign-in', credentials).pipe(
      switchMap((response: any) => {
        const decodedJwtData = this.decodeJwt(response.token);

        // Store tokens
        this.accessToken = response.token;
        this.refreshToken = response.refreshToken;
        this._authenticated = true;

        return this.getUserDetails(decodedJwtData);
      }),
      catchError(error => {
        console.error('Sign-in failed:', error);
        return throwError(() => new Error('Sign-in failed.'));
      })
    );
  }

  signInWithGoogle(redirectUrl?: string): void {
    const targetUrl = redirectUrl
      ? `${this.authUri}api/auth/google-sign-in?redirectUrl=${encodeURIComponent(redirectUrl)}`
      : `${this.authUri}api/auth/google-sign-in`;

    window.location.href = targetUrl;
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<boolean> {
    if (!this.accessToken) {
      return of(false);
    }

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        map(() => true)
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return this._httpClient
        .post(this.authUri + 'api/auth/refresh-token', {
          accessToken: this.accessToken,
          refreshToken: this.refreshToken
        })
        .pipe(
          switchMap((response: any) => {
            if (response?.accessToken && response?.refreshToken) {
              this.accessToken = response.accessToken;
              this.refreshToken = response.refreshToken;

              const decodedJwtData = this.decodeJwt(response.accessToken);
              this._authenticated = true;

              return this.getUserDetails(decodedJwtData);
            }

            return of(false);
          }),
          catchError(error => {
            console.error('Error during token refresh:', error);
            return of(false);
          }),
          finalize(() => {
            this.refreshTokenInProgress = false;
          })
        );
    } else {
      const decodedJwtData = this.decodeJwt(this.accessToken);
      this._authenticated = true;
      return this.getUserDetails(decodedJwtData);
    }
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Set the authenticated flag to false
    this._authenticated = false;

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/unlock-session', credentials);
  }

  generateRefreshToken(): Observable<any> {
    return this._httpClient
      .post(
        this.authUri + 'api/auth/refresh-token',
        {
          refreshToken: this.refreshToken,
          accessToken: this.accessToken
        },
         HttpHeadersUtil.prepareDefaultHeaders()
      )
      .pipe(map(res => res as any));
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.token || this.token.trim() === '') {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.token)) {
      return of(false);
    }
    // If the access token exists and it didn't expire, sign in using it
    // Vérifiez l'authentification de l'utilisateur avec le token
    return this.signInUsingToken();
  }

  public getUserDetails(decodedJwtData) {
    // Fetch user details
    this._navigationMockApi.getAllowedDefaultNavigation(decodedJwtData.Email).subscribe(() => {});
    return this._userService.getUserByEmail(decodedJwtData.Email).pipe(
      switchMap((fetchedUser: any) => {
        if (!fetchedUser) {
          // Handle the case when fetchedUser is null
          this._authenticated = false;
          this.accessToken = '';
          this.refreshToken = '';
          return of(fetchedUser);
        }
        const userId = fetchedUser.id;
        const userProfiles =
          fetchedUser.userProfiles?.length > 0
            ? of(fetchedUser.userProfiles)
            : this._userProfileService.getAllByUser(userId).pipe(
                switchMap((userProfiles: any[]) => {
                  let profiles = [];
                  userProfiles.forEach(item => {
                    profiles.push(item.userProfile);
                  });
                  return of(profiles);
                })
              );

        return userProfiles.pipe(
          map((profiles: UserProfile[]) => {
            fetchedUser.userProfiles = profiles;

            const allPermissions = this.mergePermissions(profiles);

            const connectedUser: UserStore = {
              id: userId,
              email: decodedJwtData.Email,
              fullname:
                decodedJwtData.FirstName.charAt(0).toUpperCase() +
                decodedJwtData.FirstName.slice(1).toLowerCase() +
                ' ' +
                decodedJwtData.LastName.charAt(0).toUpperCase() +
                decodedJwtData.LastName.slice(1).toLowerCase(),
              role: decodedJwtData.role,
              status: decodedJwtData.status,
              tenantId: decodedJwtData.TenantId,
              tenant: decodedJwtData.Tenant,
              firstname: decodedJwtData.FirstName,
              lastname: decodedJwtData.LastName,
              enabled: Boolean(decodedJwtData.IsEnabled),
              isOnboarded: Boolean(decodedJwtData.IsOnboarded),
              audience: decodedJwtData.aud,
              avatar: decodedJwtData.avatar || undefined,
              permissions: allPermissions
            };

            // Merge the same connected user properties into fetchedUser for convenience
            fetchedUser = {
              ...fetchedUser,
              email: decodedJwtData.Email,
              fullname:
                decodedJwtData.FirstName.charAt(0).toUpperCase() +
                decodedJwtData.FirstName.slice(1).toLowerCase() +
                ' ' +
                decodedJwtData.LastName.charAt(0).toUpperCase() +
                decodedJwtData.LastName.slice(1).toLowerCase(),
              role: decodedJwtData.role,
              status: decodedJwtData.status,
              tenantId: decodedJwtData.TenantId,
              tenant: decodedJwtData.Tenant,
              firstname: decodedJwtData.FirstName,
              lastname: decodedJwtData.LastName,
              enabled: Boolean(decodedJwtData.IsEnabled),
              isOnboarded: Boolean(decodedJwtData.IsOnboarded),
              audience: decodedJwtData.aud,
              avatar: decodedJwtData.avatar || fetchedUser.avatar,
              permissions: allPermissions
            };

            this._userService.user = fetchedUser;

            this._store.dispatch(setConnectedUser({ user: connectedUser }));

            if (!fetchedUser.enabled) {
              throw new Error('User is disabled.');
            }

            return fetchedUser;
          })
        );
      })
    );
  }
}
